import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ColorPickerDropdown from 'ui-library/dropdowns/ColorPickerDropdown';
import {ElementMouseEvent} from 'util/Types';

export interface ImageToolbarProps {
  isEditingDisabled: boolean;
  isSavingDisabled: boolean;
  handleCancel?: (e: ElementMouseEvent) => void;
  handleSaveRotateButtonClick?: (e: ElementMouseEvent) => void;
  handleRotateLeftButtonClick: (e: ElementMouseEvent) => void;
  handleRotateRightButtonClick: (e: ElementMouseEvent) => void;
  handleAddTextButtonClick: (e: ElementMouseEvent) => void;
  handleAddEllipseButtonClick: (e: ElementMouseEvent) => void;
  handleAddArrowButtonClick: (e: ElementMouseEvent) => void;
  handleChangeColorButtonClick: (type: 'fill' | 'stroke', color: string) => void;
  stroke: string | null;
  fill: string | null;
}

export default function ImageToolbar({
  isEditingDisabled,
  isSavingDisabled,
  handleCancel,
  handleSaveRotateButtonClick,
  handleRotateLeftButtonClick,
  handleRotateRightButtonClick,
  handleAddTextButtonClick,
  handleAddEllipseButtonClick,
  handleAddArrowButtonClick,
  handleChangeColorButtonClick,
  stroke,
  fill,
}: ImageToolbarProps): JSX.Element {
  return (
    <div className={css(styles.container)} {...getTestProps({component: 'ImageToolbar', element: 'root'})}>
      <Button
        aria-label="Rotate left"
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        className="toolbar-rotate-left"
        disabled={isEditingDisabled}
        icon="rotate-left"
        onClick={handleRotateLeftButtonClick}
        size={ButtonSize.MINI}
      />
      <Button
        aria-label="Rotate right"
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        className="u-mini-spacing-left toolbar-rotate-right"
        disabled={isEditingDisabled}
        icon="rotate-right"
        onClick={handleRotateRightButtonClick}
        size={ButtonSize.MINI}
      />
      {handleAddTextButtonClick ? (
        <Button
          aria-label="Add text"
          buttonStyle={ButtonStyle.TERTIARY_BLUE}
          className="u-mini-spacing-left toolbar-add-text"
          disabled={isEditingDisabled}
          icon="text"
          onClick={handleAddTextButtonClick}
          size={ButtonSize.MINI}
        />
      ) : null}
      {handleAddEllipseButtonClick ? (
        <Button
          aria-label="Add ellipse"
          buttonStyle={ButtonStyle.TERTIARY_BLUE}
          className="u-mini-spacing-left toolbar-add-ellipse"
          disabled={isEditingDisabled}
          icon="circle"
          onClick={handleAddEllipseButtonClick}
          size={ButtonSize.MINI}
        />
      ) : null}
      {handleAddArrowButtonClick ? (
        <Button
          aria-label="Add arrow"
          buttonStyle={ButtonStyle.TERTIARY_BLUE}
          className="u-mini-spacing-left toolbar-add-arrow"
          disabled={isEditingDisabled}
          icon="arrow-right"
          onClick={handleAddArrowButtonClick}
          size={ButtonSize.MINI}
        />
      ) : null}
      {handleChangeColorButtonClick ? (
        <ColorPickerDropdown
          className="u-mini-spacing-left toolbar-color-picker"
          buttonStyle={ButtonStyle.TERTIARY_BLUE}
          disabled={isEditingDisabled}
          foregroundName="Stroke"
          backgroundName="Fill"
          icon="palette"
          foregroundColorLink={{
            value: stroke,
            requestChange: (color) => {
              handleChangeColorButtonClick('stroke', color);
            },
          }}
          backgroundColorLink={{
            value: fill,
            requestChange: (color) => {
              handleChangeColorButtonClick('fill', color);
            },
          }}
        />
      ) : null}
      <div className={css(styles.endButtons)}>
        {handleCancel && (
          <Button
            aria-label="Cancel"
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            className="u-mini-spacing-right toolbar-cancel"
            onClick={handleCancel}
            disabled={false}
            size={ButtonSize.MINI}
          >
            Cancel
          </Button>
        )}
        {handleSaveRotateButtonClick && (
          <Button
            aria-label="Save"
            buttonStyle={ButtonStyle.PRIMARY}
            className="toolbar-save"
            onClick={handleSaveRotateButtonClick}
            disabled={isSavingDisabled}
            size={ButtonSize.MINI}
          >
            Save
          </Button>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'row',
  },
  endButtons: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
    marginLeft: '12px',
  },
});
